import React, { useEffect, useRef, useState } from "react";
import { useDocumentTitle } from "../../../../hooks/customDocumentTitleHook";
import { useLocation, useNavigate } from "react-router-dom";
import { exchangeDate, getPractice } from "../../../../helpers";
import { fetchSinglePatientIntakeForm } from "../../../../services/forms";

export default function PatientIntakePrint({ ...props }) {
  const {    
    formViewId,
    formViewSize = "A4",
    setFormView = () => {}
  } = props;

  const [formData, setFormData] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const navigate = useNavigate();
  const contentRef = useRef();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const accountId = getPractice("account_id");
  const locationId = getPractice("location_id");

  let formId = formViewId;
  let formSize = formViewSize;
  if (queryParams.get('form_id')) {
    formId = queryParams.get('form_id');
    formSize = queryParams.get('size');
  } 

  useEffect(() => {
    if(formId !== null){
      getReferralData( formId );
    } else {
      navigate(-1);
    }
  }, [formId]);

  const getReferralData = async ( formId ) => {
    const response = await fetchSinglePatientIntakeForm( accountId, locationId, formId );
    if (response) {
      if(response.data === null) {
        window.close();
      }
      setFormData(response?.data);
      setIsDataLoaded(true);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    if (isDataLoaded && queryParams.get('form_id')) {
      handlePrint();
    }
  }, [isDataLoaded, queryParams]);

  const handleCloseWindow = () => {
    window.close();
  };

  const handleFormPrint = (formId) => {
    setFormView();
    const url = `/forms/patient-intake-form/print?form_id=${formId}&size=A4`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    const pageSize = formSize == "A4" ? "A4" : "Letter";
    const styleElement = document.createElement("style");
    styleElement.innerHTML = `
      @media print {
        @page {
          size: ${pageSize};
        }
      }
    `;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [formSize]);

  useDocumentTitle("Patient Intake Form");

  return (
    <div ref={contentRef} className="docpro_signout_wrap">
      <div className="docpro_signout">
        <div>
          <div>
            <h3 className="text-center">Patient Intake Form</h3>
          </div>

          <div>
            <h5 className="mt-4 fw-bold">Patient Information</h5>
          </div>

          <div className="docpro_grid docpro_grid_gap_4 docpro_grid_col_2">
            <div className="px-0 docpro_grid_item">
              <ul className="docpro_list_1">
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Location</p><span className="mx-1">:</span> {formData?.location_id}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Last Name</p><span className="mx-1">:</span> {formData?.last_name}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>First Name</p><span className="mx-1">:</span> {formData?.first_name}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Date of Birth</p><span className="mx-1">:</span> {exchangeDate(formData?.dob)}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Email</p><span className="mx-1">:</span> {formData?.email}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Address</p><span className="mx-1">:</span> {formData?.address}
                </li>
              </ul>
            </div>
            <div className="px-0 docpro_grid_item">
              <ul className="docpro_list_1">
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>City</p><span className="mx-1">:</span> {formData?.city}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>State</p><span className="mx-1">:</span> {formData?.state}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Zip</p><span className="mx-1">:</span> {formData?.zip}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Home #</p> <span className="mx-1">:</span> {formData?.home_num}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Cell #</p> <span className="mx-1">:</span> {formData?.cell_num}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Pharmacy</p> <span className="mx-1">:</span> {formData?.pharmacy}
                </li>
              </ul>
            </div>
          </div>

          <div>
            <h5 className="mt-4 fw-bold">Primary Care Information</h5>
          </div>
          <div className="docpro_grid docpro_grid_gap_4 docpro_grid_col_2">
            <div className="px-0 docpro_grid_item">
              <ul className="docpro_list_1">
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Physician</p><span className="mx-1">:</span> {formData?.pcph}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Phone</p><span className="mx-1">:</span> {formData?.pcph_phone}
                </li>
              </ul>
            </div>
            <div className="px-0 docpro_grid_item">
              <ul className="docpro_list_1">
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Date Last Seen</p><span className="mx-1">:</span> {exchangeDate(formData?.lseen_date)}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Address</p><span className="mx-1">:</span> {formData?.address2}
                </li>
              </ul>
            </div>
          </div>

          <div>
            <h5 className="mt-4 fw-bold">Employer Information</h5>
          </div>
          <div className="docpro_grid docpro_grid_gap_4 docpro_grid_col_2">
            <div className="px-0 docpro_grid_item">
              <ul className="docpro_list_1">
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Employer</p><span className="mx-1">:</span> {formData?.employer}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Phone</p><span className="mx-1">:</span> {formData?.emp_phone}
                </li>
              </ul>
            </div>
            <div className="px-0 docpro_grid_item">
              <ul className="docpro_list_1">
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Address</p><span className="mx-1">:</span> {formData?.address3}
                </li>
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>SSN #</p><span className="mx-1">:</span> {formData?.ss_num}
                </li>
              </ul>
            </div>
          </div>
          <div className="row">
            <div className="px-0" style={{ width: '100%' }}>
              <ul className="docpro_list_1">
                <li className="flex items-center pb-1">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Sex</p><span className="mx-1 mr-2">:</span> 
                  <input className="form-check-input" type="checkbox" checked={formData?.sex == 1} disabled />
                  <span className="pr-2 mr-2">Male</span>
                  <input className="form-check-input" type="checkbox" checked={formData?.sex == 0} disabled />
                  <span>Female</span>
                </li>
                <li className="flex items-center pb-1" style={{ flexWrap: 'wrap' }}>
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 100 }}>Marital Status</p><span className="mx-1 mr-2">:</span>
                  <input className="form-check-input" type="checkbox" checked={formData?.mari_sta == 1} disabled />
                  <span className="pr-2 mr-2">Single</span>
                  <input className="form-check-input" type="checkbox" checked={formData?.mari_sta == 2} disabled />
                  <span className="pr-2 mr-2">Married</span>
                  <input className="form-check-input" type="checkbox" checked={formData?.mari_sta == 3} disabled />
                  <span className="pr-2 mr-2">Widowed</span>
                  <input className="form-check-input" type="checkbox" checked={formData?.mari_sta == 4} disabled />
                  <span>Divorced</span>
                </li>
              </ul>
            </div>
          </div>

          <div>
            <h5 className="mt-4 fw-bold">Primary Insurance</h5>
          </div>
          <ul className="docpro_list_1">
            <li className="pb-1 flex">
              <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Insurance Name</p><span className="mx-1">:</span> {formData?.pri_insurance}
            </li>
            <li className="pb-1 flex mb-1">
              <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Subscriber Name</p><span className="mx-1">:</span> {formData?.sub_name}
            </li>
          </ul>
          <ul className="docpro_list_1">
            <li className="flex items-center pb-1 mb-1" style={{ flexWrap: 'wrap' }}>
              <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Relationship to insured</p><span className="mx-1 mr-2">:</span>
              <input className="form-check-input" type="checkbox" checked={formData?.rel_insu == 1} disabled />
              <span className="pr-2 mr-2">Spouse</span>
              <input className="form-check-input" type="checkbox" checked={formData?.rel_insu == 2} disabled />
              <span className="pr-2 mr-2">Child</span>
              <input className="form-check-input" type="checkbox" checked={formData?.rel_insu == 3} disabled />
              <span className="pr-2 mr-2">Self</span>
              <input className="form-check-input" type="checkbox" checked={formData?.rel_insu == 4} disabled />
              <span>Other</span>
            </li>
          </ul>
          <ul className="docpro_list_1">
            <li className="pb-1 flex pt-0">
              <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Phone</p><span className="mx-1">:</span> {formData?.ins_phone}
            </li>
            <li className="pb-1 flex pt-0">
              <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Date of Birth</p><span className="mx-1">:</span> {exchangeDate(formData?.ins_dob)}
            </li>
            <li className="flex items-center pb-1">
              <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Sex</p><span className="mx-1 mr-2">:</span> 
              <input className="form-check-input" type="checkbox" checked={formData?.sex_insu == 1} disabled />
              <span className="pr-2 mr-2">Male</span>
              <input className="form-check-input" type="checkbox" checked={formData?.sex_insu == 0} disabled />
              <span>Female</span>
            </li>
            <li className="pb-1 flex pt-0">
              <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Address</p><span className="mx-1">:</span> {formData?.ins_address}
            </li>
            <li className="pb-1 flex pt-0 mb-1">
              <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Insurance ID</p><span className="mx-1">:</span> {formData?.ins_id}
            </li>
            <li className="pb-1 flex pt-0 mb-1">
              <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Group ID #</p><span className="mx-1">:</span>{formData?.group_id}
            </li>
          </ul>
          <ul className="docpro_list_1">
            <li className="flex ps-0">
              <p className="mb-0 pe-2 fw-bold" style={{ minWidth: 162 }}>Upload ID</p><span className="mx-1">:</span>
              <div className="bg-light rounded p-1" style={{ height: 90, width: 177 }}>
                {formData?.upload_id !== null &&
                  <img src={`${formData?.upload_id}`} className="img-fluid d-block img_print" alt="signature" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                }
              </div>
            </li>
          </ul>

          {formData?.insured2 == 1 &&
            <>
              {formSize == 'A4' ?
                <div className="spacing_2"></div>
              :
                <div className="spacing_16"></div>
              }
              <div>
                <h5 className="mt-4 fw-bold">Secondary Insurance</h5>
              </div>
              <ul className="docpro_list_1">
                <li className="pb-1 flex">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Insurance Name</p><span className="mx-1">:</span> {formData?.sec_insurance}
                </li>
                <li className="pb-1 flex mb-1">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Subscriber Name</p><span className="mx-1">:</span> {formData?.sub_name2}
                </li>
              </ul>
              <ul className="docpro_list_1">
                <li className="flex items-center pb-1 mb-1">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Relationship to insured</p><span className="mx-1 mr-2">:</span>
                  <input className="form-check-input" type="checkbox" checked={formData?.rel_insu2 == 1} disabled />
                  <span className="pr-2 mr-2">Spouse</span>
                  <input className="form-check-input" type="checkbox" checked={formData?.rel_insu2 == 2} disabled />
                  <span className="pr-2 mr-2">Child</span>
                  <input className="form-check-input" type="checkbox" checked={formData?.rel_insu2 == 3} disabled />
                  <span className="pr-2 mr-2">Self</span>
                  <input className="form-check-input" type="checkbox" checked={formData?.rel_insu2 == 4} disabled />
                  <span>Other</span>
                </li>
              </ul>
              <ul className="docpro_list_1">
                <li className="pb-1 flex pt-0">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Phone</p><span className="mx-1">:</span> {formData?.ins_phone2}
                </li>
                <li className="pb-1 flex pt-0">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Date of Birth</p><span className="mx-1">:</span> {exchangeDate(formData?.ins_dob2)}
                </li>
                <li className="flex items-center pb-1">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Sex</p><span className="mx-1 mr-2">:</span> 
                  <input className="form-check-input" type="checkbox" checked={formData?.sex_insu2 == 1} disabled />
                  <span className="pr-2 mr-2">Male</span>
                  <input className="form-check-input" type="checkbox" checked={formData?.sex_insu2 == 0} disabled />
                  <span>Female</span>
                </li>
                <li className="pb-1 flex pt-0">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Address</p><span className="mx-1">:</span> {formData?.ins_address2}
                </li>
                <li className="pb-1 flex pt-0 mb-1">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Insurance ID</p><span className="mx-1">:</span> {formData?.ins_id2}
                </li>
                <li className="pb-1 flex pt-0 mb-1">
                  <p className="mb-0 pe-3 fw-bold" style={{ minWidth: 162 }}>Group ID #</p><span className="mx-1">:</span> {formData?.group_id2}
                </li>
              </ul>
              <ul className="docpro_list_1">
                <li className="flex ps-0">
                  <p className="mb-0 pe-2 fw-bold" style={{ minWidth: 162 }}>Upload ID</p><span className="mx-1">:</span>
                  <div className="bg-light rounded p-1" style={{ height: 80, width: 167 }}>
                    {formData?.upload_id2 !== null &&
                      <img src={`${formData?.upload_id2}`} className="img-fluid d-block img_print" alt="signature" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    }
                  </div>
                </li>
              </ul>
            </>
          }

          <div className="col-12 mt-4 mx-3">
            <p className="m-0" style={{ fontSize: 13, textTransform: 'uppercase' }}><b>Please Read and sign</b></p>
          </div>

          <div className="flex" style={{ alignItems: 'start' }}>
            <input className="form-check-input" style={{ marginTop: '0.25em' }} type="checkbox" checked={formData?.accept == 1} disabled />
            <span className="form-check-label" htmlFor="agreement">
            The above information is correct to the best of my knowledge. I understand that throughout my treatment. I am responsible for notifying the physician and/or medical staff of any and all updates to the information listed above.
            </span>
          </div>
        </div>
      </div>

      <div className="docpro_invoice_btns docpro_hide_print">
        <button
          className="docpro_invoice_btn docpro_color3 close_button"
          onClick={() => {
            if (queryParams.get('form_id')) {
              handleCloseWindow();
            } else {
              setFormView();
            }
          }}
        >
          <span className="docpro_btn_icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </span>
          <span className="docpro_btn_text">Close</span>
        </button>

        <button
          className="docpro_invoice_btn docpro_color1"
          onClick={() => {
            if (queryParams.get('form_id')) {
              handlePrint();
            } else {
              handleFormPrint(formViewId);
            }
          }}
        >
          <span className="docpro_btn_icon">
            <svg xmlns="http://www.w3.org/2000/svg" className="ionicon" viewBox="0 0 512 512" >
              <path d="M384 368h24a40.12 40.12 0 0040-40V168a40.12 40.12 0 00-40-40H104a40.12 40.12 0 00-40 40v160a40.12 40.12 0 0040 40h24" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={32} />
              <rect x={128} y={240} width={256} height={208} rx="24.32" ry="24.32" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={32} /> 
              <path d="M384 128v-24a40.12 40.12 0 00-40-40H168a40.12 40.12 0 00-40 40v24" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={32} /> 
              <circle cx={392} cy={184} r={24} fill="currentColor" /> 
            </svg>
          </span>
          <span className="docpro_btn_text">Print</span>
        </button>
      </div>
    </div>
  );
}
